import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Slider,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TabIcon from "@mui/icons-material/Tab";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import LayersClearIcon from "@mui/icons-material/LayersClear";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import { StyleSettings } from "../../styles/StyleSettings";
import { TabContext } from "@mui/lab";
import React, {
  ComponentType,
  MouseEvent,
  Suspense,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Header from "./Header";
import LeftMenu from "./LeftMenu";
import CloseIcon from "@mui/icons-material/Close";
import OpacityIcon from "@mui/icons-material/Opacity";
import Footer from "./Footer";
import { useLocation } from "react-router-dom";
import TabToolbar from "./TabToolbar";
import TabBar, { TabItem } from "./TabBar";
import { NumberSize, Resizable, ResizeCallback } from "re-resizable";
import { Direction } from "re-resizable/lib/resizer";
import { ChatSearchPanelHandle, menuInfo } from "@shared/type/CommonType";
import Dashboard from "../pages/Dashboard/Dashboard";
import Chatbot from "../pages/Chatbot/Chatbot";
import ChatTip from "../pages/Chatbot/ChatTip";
import CommonSpinner from "@widgets/CommonSpinner";
import ChatCopyProgress from "@widgets/ChatCopyProgress";
import { showDialog } from "@widgets/CommonDialog";
import ErrorBoundary from "@shared/components/ErrorBoundary";
import { callApi, fnLoginCheck } from "@shared/api/restApi";
import { apiFetch } from "@shared/api/api";
import { useNavigate } from "react-router-dom";
import {
  footerMsgStore,
  globalMenu,
  menuChangeStore,
  menuInitStore,
  onSearchStore,
  useMenuStore,
  useShareStore,
} from "@shared/libs/Global";
import { SharePayload } from "@shared/type/ShareType";
import { global } from "@shared/libs/Global";
import { useNavigationStore } from "@shared/libs/Global";
import DeployNotificationListener from "../deploy/DeployNotificationListener";
import FcmPushInitializer from "../push/FcmPushInitializer";
// import ReactDOM from "react-dom/client";
// import {
//   createHtmlPortalNode,
//   InPortal,
//   OutPortal,
// } from "react-reverse-portal";

declare const require: {
  context(
    path: string,
    deep?: boolean,
    filter?: RegExp
  ): {
    keys: () => string[];
    <T>(id: string): T;
  };
};

type ModuleType = { default: ComponentType<any> };

/** 헤더 아이콘으로 여는 화면 - 탭을 만들지 않고 홈(대시보드) 자리에 표시한다 */
type HomeView = {
  key: string;
  title: string;
  path: string;
  data: any;
  ref: any;
  component: React.ReactNode;
};

/**
 * 탭을 만들지 않고 홈(대시보드) 자리에 띄우는 화면들 - 헤더 아이콘 화면.
 * 아이콘/좌측 메뉴/메뉴검색/위젯 어디서 열든 여기 한 군데로 모인다.
 * (탭과 홈에 같은 화면이 두 번 마운트되면 조회·저장 상태가 갈리고,
 *  cd_menu로 등록되는 챗봇 화면 컨텍스트도 서로 덮어쓴다)
 */
const HOME_MENU_CODES = ["SN_SYS_WKF_REP_001", "SN_SYS_DSH_REP_001"];

/** 핀 고정한 메뉴코드 저장 키(새로고침·재로그인 후에도 유지) */
const PINNED_TABS_KEY = "pinnedTabs";

const loadPinnedKeys = (): string[] => {
  try {
    const saved = localStorage.getItem(PINNED_TABS_KEY);
    const parsed = saved ? JSON.parse(saved) : [];
    return Array.isArray(parsed)
      ? parsed.filter((key) => typeof key === "string")
      : [];
  } catch {
    return [];
  }
};

// 탭 우클릭 메뉴 스타일 - 탭 바와 같은 남색/회색 테두리, 전역 폰트 크기를 따른다
const CONTEXT_MENU_NAVY = "rgb(11, 12, 105)";
const CONTEXT_MENU_BORDER = "rgb(186, 191, 199)";
// 파스텔 3단계 - 헤더 밴드(가장 옅음) < 호버 < 보고 있는 탭(가장 진함)
// 밴드와 활성 항목이 같은 톤이면 어느 게 현재 화면인지 구분이 안 된다.
const CONTEXT_MENU_PASTEL = "rgb(233, 236, 251)";
const CONTEXT_MENU_PASTEL_HOVER = "rgb(222, 227, 248)";
const CONTEXT_MENU_ACTIVE = "rgb(191, 202, 245)";
const CONTEXT_MENU_ACTIVE_HOVER = "rgb(178, 191, 242)";
// 헤더 밴드 - 남색을 옅게 깐 파스텔 그라데이션(글씨는 남색)
const CONTEXT_MENU_GRADIENT = `linear-gradient(90deg, ${CONTEXT_MENU_PASTEL} 0%, rgb(244, 246, 253) 100%)`;

const contextMenuPaperSx = {
  border: `1px solid ${CONTEXT_MENU_BORDER}`,
  borderRadius: "8px",
  boxShadow: "0 8px 22px rgba(11, 12, 105, 0.18)",
  overflow: "hidden", // 헤더 밴드가 모서리까지 꽉 차도록
  mt: 0.5,
};

/** 메뉴 상단 색 밴드 - 어떤 탭 기준인지 보여준다 */
const contextMenuHeaderSx = {
  background: CONTEXT_MENU_GRADIENT,
  color: CONTEXT_MENU_NAVY,
  borderBottom: `1px solid ${CONTEXT_MENU_PASTEL_HOVER}`,
  px: 1.5,
  py: 0.9,
  fontSize: `${StyleSettings.font}px`,
  fontWeight: "bold",
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/** 메뉴 하단 요약줄 */
const contextMenuFooterSx = {
  borderTop: `1px solid ${CONTEXT_MENU_BORDER}`,
  backgroundColor: "rgb(247, 249, 253)",
  color: "rgb(120, 124, 135)",
  px: 1.5,
  py: 0.6,
  fontSize: `${StyleSettings.font - 1}px`,
  display: "flex",
  justifyContent: "space-between",
  gap: 1,
};

const contextMenuItemSx = {
  fontSize: `${StyleSettings.font}px`,
  minHeight: "34px",
  px: 1.5,
  // 호버 시 왼쪽에 남색 액센트 바(자리를 미리 잡아 글자가 밀리지 않게 한다)
  borderLeft: "3px solid transparent",
  "& .MuiListItemIcon-root": { minWidth: "26px", color: CONTEXT_MENU_NAVY },
  "& .MuiListItemText-primary": { fontSize: `${StyleSettings.font}px` },
  "&:hover": {
    backgroundColor: "rgba(11, 12, 105, 0.08)",
    borderLeftColor: CONTEXT_MENU_NAVY,
  },
  "&.Mui-disabled .MuiListItemIcon-root": { color: "rgb(151, 151, 151)" },
};

/**
 * 홈 영역 패널의 표시/숨김 - 탭 패널과 같은 방식으로 겹쳐 놓고 시각·입력만 토글한다.
 * display:none으로 숨기면 안에 있는 그리드가 크기 0으로 재측정되면서
 * ag-grid가 setState 루프에 빠진다(Maximum update depth).
 */
const homePaneStyle = (isActive: boolean): React.CSSProperties => ({
  position: "absolute",
  inset: 0,
  overflowY: "auto",
  opacity: isActive ? 1 : 0,
  visibility: isActive ? "visible" : "hidden",
  pointerEvents: isActive ? "auto" : "none",
});
const modules = require.context("@menu", true, /\.tsx$/);

/**
 * 메뉴 파일을 찾는다. 경로(모듈/nm_file/nm_file)로 못 찾으면 파일 이름만으로 다시 찾는다.
 * 메뉴디자이너로 만든 메뉴는 nm_file 이 SN_COM_LAYOUT(공용 페이지) 인데 파일은 COM 폴더에 하나뿐이라
 * 모듈 폴더(PUR/SN_COM_LAYOUT/…)에는 없다 (2026-09-29).
 */
const resolveModulePath = (filePath: string): string | undefined => {
  const keys = modules.keys();
  const exact = keys.find((key) => key.endsWith(`${filePath}.tsx`));
  if (exact) return exact;
  const name = filePath.split("/").pop();
  return name ? keys.find((key) => key.endsWith(`/${name}/${name}.tsx`)) : undefined;
};

// TabContent 컴포넌트 정의
const TabContent = React.memo(
  ({ component }: { component: React.JSX.Element }) => {
    return <>{component}</>;
  }
);

// 공유 조회조건 주입 재시도 설정.
// SearchPanel은 화면 초기값(setFormData)이 data prop으로 내려올 때 폼을 재동기화하므로,
// 주입 직후 값이 살아있는지 확인하고 덮어써졌으면 다시 주입한다.
const SHARE_INJECT_TRIES = 5;
const SHARE_INJECT_DELAY = 200;

const waitFor = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * 공유 payload의 조회조건을 조회패널에 주입하고 화면 조회를 실행한다.
 * 값이 확정된 뒤에만 조회해서, 초기값에 덮어써진 조건으로 조회되는 것을 막는다.
 */
const applySharedSearchAndRun = async (
  panel: ChatSearchPanelHandle,
  ref: any,
  payload: SharePayload
) => {
  const shared = {
    ...(payload.state?.search || {}),
    ...(payload.state?.params || {}),
  };
  // 조회패널에 없는 필드는 주입하지 않는다(챗봇과 동일한 화이트리스트)
  const allowed = new Set(panel.getAllowedFieldNames?.() || []);
  const patch: Record<string, any> = {};
  Object.entries(shared).forEach(([key, value]) => {
    if (allowed.has(key)) patch[key] = value;
  });

  const isApplied = () => {
    const current: Record<string, any> = panel.getFormdata?.() || {};
    return Object.entries(patch).every(
      ([key, value]) => String(current[key] ?? "") === String(value ?? "")
    );
  };

  if (Object.keys(patch).length > 0) {
    let applied = false;
    for (let i = 0; i < SHARE_INJECT_TRIES && !applied; i++) {
      panel.setFormdata({ ...(panel.getFormdata?.() || {}), ...patch });
      await waitFor(SHARE_INJECT_DELAY);
      applied = isApplied();
    }
    if (!applied) {
      // 화면 초기값이 계속 덮어쓰는 경우 — 잘못된 조건으로 조회하지 않고 후퇴한다
      console.warn("share search params were overwritten by screen init");
      return;
    }
  }

  try {
    await ref.onSearch();
  } catch (error) {
    // 조회 실패는 메뉴 오픈까지만 된 상태로 조용히 후퇴한다
    console.error("share fallback search failed", error);
  }
};

export default function MainLayout() {
  const location = useLocation();
  const MENU_ROUTE_ALLOW_KEY = "allow_menu_route_once";
  const JUST_LOGGED_IN_KEY = "just_logged_in";
  const SKIP_MENU_ROUTE_KEY = "skip_menu_route_once";
  const SESSION_FLAG_TRUE = "true";
  const [tabs, setTabs] = useState<TabItem[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [menuDatas, setMenuDatas] = useState<any[]>([]);
  const [componentRefs, setComponentRefs] = useState<any[]>([]);
  const [, setRefVersion] = useState(0);

  // 단위화면은 lazy 로딩이라 ref.current가 마운트 이후에 채워진다.
  // 툴바가 useImperativeHandle로 노출된 함수 목록을 바로 읽을 수 있도록,
  // ref가 채워지는 순간 리렌더를 일으킨다.
  //
  // 단, 대부분의 단위화면이 deps 배열 없이 useImperativeHandle을 호출해
  // 매 렌더마다 새 객체가 대입된다. 그때마다 리렌더를 걸면 화면 입력 하나하나가
  // MainLayout 전체 리렌더로 번지므로, "노출된 함수 이름 집합"이 실제로
  // 달라졌을 때만 알린다. (보통 마운트/언마운트 시 각 1회)
  const createComponentRef = useCallback(() => {
    let instance: any = null;
    let exposedNames = "";

    const signatureOf = (value: any) =>
      value
        ? Object.keys(value)
            .filter((key) => typeof value[key] === "function")
            .sort()
            .join(",")
        : "";

    return {
      get current() {
        return instance;
      },
      set current(next: any) {
        instance = next;
        const nextNames = signatureOf(next);
        if (nextNames === exposedNames) return;
        exposedNames = nextNames;
        setRefVersion((prev) => prev + 1);
      },
    };
  }, []);
  const [tabHeight, setTabHeight] = useState("50px");
  const [dashHeight, setDashHeight] = useState("100%");
  // 헤더 아이콘(워크플로우/대시보드)으로 연 화면들.
  // 한 번 열면 계속 마운트해 두어 다시 눌러도 조회 상태가 남아 있다.
  const [homeViews, setHomeViews] = useState<HomeView[]>([]);
  const [activeHomeKey, setActiveHomeKey] = useState<string | null>(null);
  const homeViewKeysRef = useRef<string[]>([]);
  // 홈 영역이 실제로 보이는 상태(활성 탭 없음)에서만 홈 화면을 "지금 보고 있는 화면"으로 친다.
  // 탭을 보고 있는데도 홈 화면에 물려 있으면 툴바가 홈 화면을 조작하고,
  // 더 나쁘게는 툴바가 전역 activeMenuInfo를 홈 화면 코드로 세팅해 둬서
  // 그 뒤에 여는 화면이 getInitFormData()로 엉뚱한 메뉴의 설정을 받아온다
  // (컬럼 설정이 어긋나 그리드가 undefined를 받고 화면 전체가 죽는다).
  const activeHomeView =
    activeTab === -1
      ? (homeViews.find((view) => view.key === activeHomeKey) ?? null)
      : null;
  // 툴바에 넘기는 배열은 반드시 렌더마다 같은 참조여야 한다.
  // 매번 새 배열을 만들면 TabToolbar의 effect(deps: componentRefs/menuData)가
  // 렌더마다 돌면서 setOnSearch -> 스토어 갱신 -> 리렌더 루프가 된다.
  const homeToolbarRefs = useMemo(
    () => (activeHomeView ? [activeHomeView.ref] : null),
    [activeHomeView]
  );
  const homeToolbarMenuData = useMemo(
    () => (activeHomeView ? [activeHomeView.data] : null),
    [activeHomeView]
  );
  const [loadingMenu, setLoadingMenu] = useState(new Map()); // 로딩 상태 관리용 map
  const initializingMenus = menuInitStore((state) => state.initializingMenus);
  const { footerMsg } = footerMsgStore(); // footer 메세지 관리용 state
  const navigate = useNavigate();
  const { userInfo } = global();
  const { menuItems, setMenuItems } = useMenuStore();
  const setNavigate = useNavigationStore((state) => state.setNavigate);
  // Tab 관련
  const tabsRef = useRef<HTMLDivElement>(null); // Tabs 영역을 참조
  const [menuPosition, setMenuPosition] = useState<{
    mouseX: any;
    mouseY: any;
  } | null>(null);
  // 우클릭한 탭(빈 공간에서 눌렀으면 보고 있던 탭)과 "열린 메뉴" 하위 목록의 기준 위치
  const [contextTabIndex, setContextTabIndex] = useState(-1);
  // 핀 고정된 메뉴코드. 일괄 닫기에서 제외되고, 탭 목록 맨 앞(고정 구역)에 모인다.
  // 탭을 닫아도 핀은 남겨 두어 그 메뉴를 다시 열면 고정 상태로 열린다.
  const [pinnedKeys, setPinnedKeys] = useState<string[]>(loadPinnedKeys);
  const [openListAnchor, setOpenListAnchor] = useState<HTMLElement | null>(
    null
  );
  const leftMenuRef = useRef<any>(null);
  const [width, setWidth] = useState(250);
  const [logoWidth, setLogoWidth] = useState(250);
  const [prevWidth, setPrevWidth] = useState(250);
  const [path, setPath] = useState("");
  const [pathMap, setPathMap] = useState<{ [tabIndex: number]: string }>({});
  const isChatbotRoute = location.pathname === "/chatbot";
  const [isChatbotDrawerOpen, setIsChatbotDrawerOpen] = useState(false);
  /* 챗봇 투명도(0~60%) — 마우스가 챗봇 밖에 있을 때 아래 메뉴 화면이 비쳐 보인다. 이 브라우저에 기억한다 (2026-10-21) */
  const CHATBOT_CLEAR_KEY = "snotes.chatbot.transparency";
  const [chatbotClear, setChatbotClear] = useState<number>(() => {
    try {
      const saved = Number(localStorage.getItem(CHATBOT_CLEAR_KEY));
      return saved >= 0 && saved <= 60 ? saved : 0;
    } catch {
      return 0;
    }
  });
  const [clearAnchor, setClearAnchor] = useState<HTMLElement | null>(null);
  /* 챗봇 고정(📌) — 켜면 메뉴 화면을 눌러도 안 닫힌다(떠 있는 창), 끄면 챗봇 밖을 누를 때 닫힌다. 처음은 켜짐, 기억한다 (2026-10-22) */
  const CHATBOT_PIN_KEY = "snotes.chatbot.pinned";
  const [chatbotPinned, setChatbotPinned] = useState<boolean>(() => {
    try {
      return localStorage.getItem(CHATBOT_PIN_KEY) !== "N";
    } catch {
      return true;
    }
  });
  const toggleChatbotPin = () => {
    setChatbotPinned((on) => {
      try {
        localStorage.setItem(CHATBOT_PIN_KEY, on ? "N" : "Y");
      } catch {
        /* 저장소를 못 쓰는 브라우저 — 기억만 못 한다 */
      }
      return !on;
    });
  };
  /* 고정을 끈 채 열려 있으면 챗봇 밖 누름에 닫는다. 사람이 누른 것만 — 챗봇이 메뉴를 열거나 조회하는 것은 닫지 않는다.
     챗봇 창 · 챗봇이나 화면이 띄운 메뉴 · 확인창 · 도움창 · 상단 [SNotes AI Bot] 버튼은 뺀다 */
  useEffect(() => {
    if (!isChatbotDrawerOpen || chatbotPinned) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      if (
        !target ||
        target.closest?.(".snotes-chatbot-drawer, .MuiModal-root, .MuiPopover-root, [role='dialog'], [data-chatbot-toggle]")
      ) {
        return;
      }
      setIsChatbotDrawerOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [isChatbotDrawerOpen, chatbotPinned]);
  const saveChatbotClear = (value: number) => {
    try {
      localStorage.setItem(CHATBOT_CLEAR_KEY, String(value));
    } catch {
      /* 저장소를 못 쓰는 브라우저 — 기억만 못 한다 */
    }
  };
  /* 챗봇 서랍 폭 — 왼쪽 가장자리를 끌어 조절하고 이 브라우저에 기억한다 (2026-10-16). 메뉴디자이너는 따로 넓게 */
  const CHATBOT_WIDTH_KEY = "snotes.chatbot.width";
  const clampChatbotWidth = (w: number) =>
    Math.round(Math.min(Math.max(w, 380), Math.max(380, window.innerWidth * 0.8)));
  const [chatbotWidth, setChatbotWidth] = useState<number>(() => {
    try {
      const saved = Number(localStorage.getItem("snotes.chatbot.width"));
      return saved > 0 ? clampChatbotWidth(saved) : 480;
    } catch {
      return 480;
    }
  });
  const startChatbotResize = (event: React.MouseEvent) => {
    event.preventDefault();
    let latest = chatbotWidth;
    const move = (e: globalThis.MouseEvent) => {
      latest = clampChatbotWidth(window.innerWidth - e.clientX);
      setChatbotWidth(latest);
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      document.body.style.userSelect = "";
      try {
        localStorage.setItem(CHATBOT_WIDTH_KEY, String(latest));
      } catch {
        /* 저장소를 못 쓰는 브라우저 — 기억만 못 한다 */
      }
    };
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };
  // 메뉴디자이너에서는 챗봇에 좌측 작업 메뉴(새 채팅/쿼리 확인/임시저장/검색/이전 대화)가
  // 함께 뜨므로, 그만큼 드로어를 넓혀 대화창이 좁아지지 않게 한다.
  const activeMenuCode = globalMenu((state) => state.activeMenuInfo?.cd_menu);
  const isMenuDesignerActive =
    (activeMenuCode || "").trim() === "SN_SFA_ISSUE_REG_006";
  // 공유 링크 진입 시 복원할 payload 저장소
  const { pendingPayload, clearPendingPayload, setPendingPayload } =
    useShareStore();
  // 텔레그램 이슈 공유일 때만 모바일 UI 분기용 메뉴 ID
  const [telegramShareMenuId, setTelegramShareMenuId] = useState<string | null>(
    null
  );
  const menuRouteRef = useRef<string | null>(null);
  // 현재 열려 있는(또는 여는 중인) 탭의 메뉴코드 목록 — 동기 사본.
  // tabs state는 "렌더 시점 스냅샷"이라 연속 클릭이나 무거운 화면 렌더로
  // 리렌더가 밀리는 동안에는 이전 값이 잡혀 중복 판정을 놓친다.
  // 그래서 중복 오픈 판정/인덱스 계산은 항상 이 ref로 한다.
  const tabKeysRef = useRef<string[]>([]);
  // 공통 폴백으로 조회를 이미 실행한 payload 키 (중복 조회 방지)
  const shareFallbackKeyRef = useRef<string | null>(null);

  // 로그인 직후 공유 토큰이 남아있으면 여기서 payload를 복구
  useEffect(() => {
    if (pendingPayload) return;
    const token = sessionStorage.getItem("share_link_token");
    if (!token) return;

    const loadSharedPayload = async () => {
      try {
        const tenantId = userInfo?.tenant_id || "DEFAULT";
        const res = await apiFetch<{ payload: SharePayload }>(
          `/api/share/${token}`,
          {
            headers: {
              "X-TENANT-ID": tenantId,
            },
          }
        );
        const payload = res.data?.payload;
        if (payload) {
          setPendingPayload(payload);
          sessionStorage.removeItem("share_link_token");
        }
      } catch (error) {
        // 무효 토큰은 무시하고 제거
      }
    };

    loadSharedPayload();
  }, [pendingPayload, setPendingPayload, userInfo?.tenant_id]);

  const fetchLoginCheck = async () => {
    try {
      // const chk = await fnLoginCheck();
      // if (!chk) navigate("/Login");
    } catch (error) {}
  };

  useEffect(() => {
    fetchLoginCheck();
    const preventGoBack = () => {
      window.history.pushState(null, "", window.location.href);
    };

    const handleBeforeUnload = (event: any) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", preventGoBack);
    window.addEventListener("beforeunload", handleBeforeUnload);

    (window as any).__removeBeforeUnload__ = () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };

    return () => {
      window.removeEventListener("popstate", preventGoBack);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (userInfo?.tenant_name) {
      document.title = `SmartNotes - ${userInfo.tenant_name}`;
    } else {
      document.title = "SmartNotes";
    }
  }, [userInfo?.tenant_name]);

  useEffect(() => {}, [location.state]);

  useEffect(() => {
    setNavigate(navigate);
  }, [navigate, setNavigate]);

  useEffect(() => {
    if (location.pathname === "/MainLayout") {
      // 로그인 직후 플래그는 메인 진입 시 정리해 이후 /menu 진입을 막지 않도록 함
      sessionStorage.removeItem(JUST_LOGGED_IN_KEY);
      sessionStorage.removeItem(SKIP_MENU_ROUTE_KEY);
      sessionStorage.removeItem(MENU_ROUTE_ALLOW_KEY);
    }
  }, [location.pathname]);

  const memoizedTabs = useMemo(() => tabs, [tabs]);

  // 커밋된 tabs로 동기 사본을 다시 맞춘다(오픈/클로즈 외 경로로 tabs가 바뀌는 경우 대비)
  useEffect(() => {
    tabKeysRef.current = tabs.map((tab) => tab.key);
  }, [tabs]);

  const updateMenuUrl = useCallback(
    (menuId?: string) => {
      if (!menuId) {
        menuRouteRef.current = null;
        navigate("/MainLayout", { replace: true });
        return;
      }
      if (location.pathname === `/menu/${menuId}`) {
        menuRouteRef.current = menuId;
        return;
      }
      menuRouteRef.current = menuId;
      navigate(`/menu/${menuId}`, { replace: true });
    },
    [navigate, location.pathname]
  );

  /**
   * 탭을 한 번에 닫는다(한 개든 여러 개든 이 함수로 모은다).
   * 하나씩 반복 호출하면 두 번째부터는 이미 지워진 배열이 아니라
   * 같은 렌더의 옛 배열을 보고 지우기 때문에 인덱스가 어긋난다.
   */
  const closeTabsByKeys = (keysToClose: string[]) => {
    if (keysToClose.length === 0) return;
    const closing = new Set(keysToClose);

    keysToClose.forEach((key) => {
      // footer 메시지·로딩 상태를 남겨두면 같은 메뉴를 다시 열었을 때 그대로 되살아난다
      footerMsg.delete(key);
      handleIsLoading(key, false);
      if (key === telegramShareMenuId) setTelegramShareMenuId(null);
    });
    // 닫기 전에 "지금 보고 있는 탭"을 키로 기억해 둔다(인덱스는 아래에서 밀린다)
    const activeTabKey = tabs[activeTab]?.key;

    const keepIndexes = tabs
      .map((tab, index) => (closing.has(tab.key) ? -1 : index))
      .filter((index) => index !== -1);

    const filteredTabs = keepIndexes.map((index) => tabs[index]);
    setTabs(filteredTabs);
    // 닫자마자 같은 메뉴를 다시 클릭해도 "이미 열림"으로 오판하지 않도록 즉시 반영
    tabKeysRef.current = filteredTabs.map((tab) => tab.key);
    setMenuDatas(keepIndexes.map((index) => menuDatas[index]));
    setComponentRefs(keepIndexes.map((index) => componentRefs[index]));

    // pathMap은 인덱스 기준이라 남는 탭 순서대로 다시 만든다
    const newPathMap: { [tabIndex: number]: string } = {};
    keepIndexes.forEach((oldIndex, newIndex) => {
      if (pathMap[oldIndex] !== undefined) {
        newPathMap[newIndex] = pathMap[oldIndex];
      }
    });
    setPathMap(newPathMap);

    if (filteredTabs.length === 0) {
      setTabHeight("50px");
      setDashHeight("100%");
      setActiveTab(-1);
      // 헤더 아이콘 화면이 떠 있으면 그 경로를, 아니면 홈(빈 경로).
      // 이 시점엔 아직 활성 탭이 남아 있어 activeHomeView가 비므로 직접 찾는다.
      setPath(homeViews.find((view) => view.key === activeHomeKey)?.path ?? "");
      updateMenuUrl();
      return;
    }

    // 활성 탭은 인덱스가 아니라 "어느 탭이었는지"로 유지한다.
    // 앞쪽 탭을 닫으면 뒤 탭들의 인덱스가 한 칸씩 당겨지는데, 인덱스를 그대로 두면
    // 보고 있던 화면 대신 옆 탭 화면이 나온다.
    // (예전에는 화면은 activeTab, URL은 activeTab-1을 써서 서로 다른 탭을 가리켰다)
    let nextActiveTab = filteredTabs.findIndex(
      (tab) => tab.key === activeTabKey
    );
    if (nextActiveTab < 0) {
      // 보고 있던 탭이 닫힌 경우 — 그 자리로 당겨온 탭을 활성화(마지막이면 이전 탭)
      nextActiveTab = Math.min(Math.max(activeTab, 0), filteredTabs.length - 1);
    }

    setActiveTab(nextActiveTab);
    setPath(newPathMap[nextActiveTab] ?? "");

    const nextMenuId = filteredTabs[nextActiveTab]?.key;
    if (nextMenuId) {
      updateMenuUrl(nextMenuId);
    }
  };

  /** 탭 이름(메뉴 이름). 열려 있지 않으면 메뉴 목록에서, 그래도 없으면 코드 */
  const tabName = (key: string) =>
    tabs.find((tab) => tab.key === key)?.originTitle ||
    menuItems.find((menu: any) => menu.cd_menu === key)?.nm_menu ||
    key;
  const keptNotice = (kept: string[]) =>
    `${kept.map(tabName).join(" · ")}은(는) 저장하지 않은 내용이 있어 닫지 않았습니다.`;

  /**
   * 메뉴 닫기 공통 (2026-10-16) — 탭 X · 우클릭 닫기 · 챗봇이 같이 쓴다. 판단을 여기 하나에만 둔다.
   * 하나면 수정된 내용이 있을 때 "저장하시겠습니까?" — [예] 그 화면의 저장을 돌려 수정 표시가 다 없어지면 닫고,
   * [아니오] 저장하지 않고 닫고, [취소] 닫지 않는다(2026-10-23, 엑셀처럼). 여럿이면 수정 중인 메뉴는 남기고 나머지만 닫는다.
   * 저장하지 않은 내용 = 그 메뉴 그리드 · 입력폼의 수정 건수 — 각 화면이 저장 전에 "수정된 내용이 있습니다" 를 띄울 때 보는 것과 같다.
   * 예전 그리드 화면은 건수를 몰라 지금처럼 그냥 닫힌다
   */
  const closeMenus = async (
    keys: string[]
  ): Promise<{ closed: string[]; kept: string[]; cancelled: boolean }> => {
    const changed = (key: string) => menuChangeStore.getState().changedCount(key) > 0;
    if (keys.length === 0) return { closed: [], kept: [], cancelled: false };
    if (keys.length === 1) {
      if (changed(keys[0])) {
        const ref = componentRefs[tabs.findIndex((tab) => tab.key === keys[0])]?.current as any;
        if (typeof ref?.onSave !== "function") {
          /* 저장 버튼이 없는 화면 — 전처럼 닫을지만 묻는다 */
          const ok = await showDialog("confirm", `${tabName(keys[0])}에 저장하지 않은 내용이 있습니다. 닫을까요?`);
          if (!ok) return { closed: [], kept: [], cancelled: true };
        } else {
          const answer = await showDialog(
            "confirmCancel",
            `${tabName(keys[0])}에 수정된 내용이 있습니다. 저장하시겠습니까?`
          );
          /* 취소 — 닫지 않는다 */
          if (answer !== true && answer !== false) return { closed: [], kept: [], cancelled: true };
          if (answer === true && !(await saveBeforeClose(keys[0], ref))) {
            /* 저장이 안 됐다(필수값 · 확인 거절 등) — 화면이 이미 이유를 알렸다. 닫지 않는다 */
            return { closed: [], kept: [], cancelled: true };
          }
        }
      }
      closeTabsByKeys(keys);
      return { closed: keys, kept: [], cancelled: false };
    }
    const kept = keys.filter(changed);
    const closing = keys.filter((key) => !kept.includes(key));
    closeTabsByKeys(closing);
    return { closed: closing, kept, cancelled: false };
  };

  /**
   * 닫기 전 저장 — 툴바 [저장]과 같은 길(로딩 표시 포함). 성공은 "저장 뒤 수정 표시가 0건" 으로 본다 (2026-10-23).
   * 저장 함수가 성공 여부를 돌려주지 않는 화면도 있고, 저장 뒤 다시 조회해 표시를 지우는 화면도 있어 잠깐(2초까지) 기다린다
   */
  const saveBeforeClose = async (key: string, ref: any): Promise<boolean> => {
    handleIsLoading(key, true);
    handleTabTitle(key, true);
    try {
      const result = await ref.onSave();
      if (result === false) return false;
    } catch {
      return false;
    } finally {
      handleIsLoading(key, false);
      handleTabTitle(key, false);
    }
    for (let waited = 0; waited <= 2000; waited += 100) {
      if (menuChangeStore.getState().changedCount(key) === 0) return true;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return false;
  };

  /** 여러 탭 닫기(우클릭) — 남긴 탭이 있으면 한 번에 알린다 */
  const closeManyTabs = async (keys: string[]) => {
    const result = await closeMenus(keys);
    if (result.kept.length > 0) await showDialog("alter", keptNotice(result.kept));
  };

  /** 챗봇 메뉴 닫기 — 같은 공통 닫기로, 결과 한 줄을 돌려준다(챗봇 말풍선) */
  const closeMenusForChat = async (cdMenu?: string, all?: boolean): Promise<string> => {
    if (all) {
      const keys = closableKeys(tabs);
      if (keys.length === 0) return "닫을 메뉴가 없습니다.";
      const result = await closeMenus(keys);
      if (result.cancelled) return `${tabName(keys[0])} 메뉴를 닫지 않았습니다.`;
      const head =
        result.closed.length > 0 ? `메뉴 ${result.closed.length}개를 닫았습니다.` : "닫은 메뉴가 없습니다.";
      return result.kept.length > 0 ? `${head} ${keptNotice(result.kept)}` : head;
    }
    const key = cdMenu || tabs[activeTab]?.key;
    const tab = key ? tabs.find((item) => item.key === key) : undefined;
    if (!key || !tab) return key ? `${tabName(key)} 메뉴는 열려 있지 않습니다.` : "닫을 메뉴가 없습니다.";
    if (!tab.closeable) return `${tabName(key)} 메뉴는 닫을 수 없습니다.`;
    const result = await closeMenus([key]);
    return result.cancelled ? `${tabName(key)} 메뉴를 닫지 않았습니다.` : `${tabName(key)} 메뉴를 닫았습니다.`;
  };

  const handleTabClose = (tabIndex: number) => {
    const key = tabs[tabIndex]?.key;
    if (key) void closeMenus([key]);
  };

  // 드래그로 탭 순서 변경 -------------------------------------------------
  // 탭 관련 상태가 인덱스로 짝지어진 병렬 배열(tabs / menuDatas / componentRefs /
  // pathMap)이라, 하나만 옮기면 툴바(componentRefs[index])와 상단 경로가 어긋난다.
  // 그래서 네 개를 같은 규칙으로 동시에 옮기고 activeTab도 따라 보정한다.
  const moveTab = useCallback((from: number, to: number) => {
    if (from < 0 || to < 0 || from === to) return;

    const reorder = <T,>(arr: T[]): T[] => {
      const next = [...arr];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    };

    const count = tabKeysRef.current.length;
    if (from >= count || to >= count) return;

    setTabs((prev) => {
      const next = reorder(prev);
      // 중복 오픈 판정용 동기 사본도 같은 순서로 맞춘다
      tabKeysRef.current = next.map((tab) => tab.key);
      return next;
    });
    setMenuDatas((prev) => reorder(prev));
    setComponentRefs((prev) => reorder(prev));

    // pathMap은 인덱스가 키라서 배열로 펼쳐 옮긴 뒤 다시 인덱스로 접는다
    setPathMap((prev) => {
      const paths = reorder(
        Array.from({ length: count }, (_, index) => prev[index])
      );
      const next: { [tabIndex: number]: string } = {};
      paths.forEach((path, index) => {
        if (path !== undefined) next[index] = path;
      });
      return next;
    });

    // 보고 있던 탭이 그대로 활성 상태로 남도록 인덱스만 재계산한다
    setActiveTab((prev) => {
      if (prev === from) return to;
      if (from < prev && prev <= to) return prev - 1;
      if (to <= prev && prev < from) return prev + 1;
      return prev;
    });
  }, []);

  // 홈 화면이 자식에서 다른 메뉴를 열 때 쓰는 통로.
  // openHomeMenu와 handleMenuClick이 서로를 참조하므로 ref로 고리를 끊는다.
  const handleMenuClickRef = useRef<any>(null);
  const openMenuFromHome = useCallback((...args: any[]) => {
    handleMenuClickRef.current?.(...args);
  }, []);

  /**
   * 헤더 아이콘 화면(워크플로우/대시보드)을 홈(대시보드) 자리에 띄운다.
   * 탭을 만들지 않고, 한 번 연 화면은 계속 마운트해 두어 상태가 유지된다.
   */
  const openHomeMenu = useCallback(
    (
      menuKey: string,
      menuTitle: string,
      cd_module: string,
      filePath: string,
      data: any,
      menuPath: string
    ) => {
      // 처음 누른 화면만 마운트한다(이미 열려 있으면 그대로 다시 보여준다)
      if (!homeViewKeysRef.current.includes(menuKey)) {
        const resolvedPath = resolveModulePath(filePath);

        if (!resolvedPath) {
          console.error(`Component not found for path: ${filePath}`);
          return;
        }

        const Component = (modules(resolvedPath) as ModuleType).default;
        const newComponentRef = createComponentRef();
        const Menuprops: menuInfo = {
          cd_menu: menuKey,
          nm_menu: menuTitle,
          cd_module: cd_module,
          originMenu: "",
        };

        homeViewKeysRef.current = [...homeViewKeysRef.current, menuKey];
        setHomeViews((prev) =>
          prev.some((view) => view.key === menuKey)
            ? prev
            : [
                ...prev,
                {
                  key: menuKey,
                  title: menuTitle,
                  path: menuPath,
                  data,
                  ref: newComponentRef,
                  component: (
                    <Suspense fallback={<div>Loading...</div>}>
                      <Component
                        ref={newComponentRef}
                        Menuprops={Menuprops}
                        onMenuClick={openMenuFromHome}
                      />
                    </Suspense>
                  ),
                },
              ]
        );
      }

      // 탭은 건드리지 않고 홈 영역만 이 화면으로 바꾼다
      setActiveHomeKey(menuKey);
      setActiveTab(-1);
      setTabHeight("50px");
      setDashHeight("100%");
      setPath(menuPath ?? "");
      if (isChatbotRoute) {
        navigate("/MainLayout");
      }
    },
    [createComponentRef, openMenuFromHome, isChatbotRoute, navigate]
  );

  const handleMenuClick = useCallback(
    async (
      menuKey: string,
      menuTitle: string,
      cd_module: string,
      filePath: string,
      data: any,
      menuPath: string,
      nodeData?: any,
      figure?: any,
      originMenu?: any
    ) => {
      // 수동 메뉴 오픈 시 로그인 플래그로 인한 /MainLayout 되돌림 방지
      sessionStorage.removeItem(MENU_ROUTE_ALLOW_KEY);
      sessionStorage.removeItem(JUST_LOGGED_IN_KEY);
      sessionStorage.removeItem(SKIP_MENU_ROUTE_KEY);

      const params = {
        cd_firm: userInfo.cd_firm,
        cd_menu: menuKey,
        cd_user: userInfo.cd_user,
        dc_ip1: userInfo.dc_ip,
      };
      // 최근사용 메뉴 기록(addRec)은 탭 오픈을 막을 이유가 없다.
      // 예전처럼 await하면 응답이 느리거나(성능 이슈) 오류 다이얼로그가 뜬 동안
      // 중복 판정이 통째로 미뤄져서, 그 사이 들어온 같은 메뉴 클릭이
      // 모두 "열린 탭 없음"으로 판정되어 새 탭이 여러 개 생긴다.
      // sys_menu 미등록 화면(예: 메시지센터)은 최근사용 목록에 남기지 않는다.
      if (!data?.skipRec) {
        void callApi("addRec", params);
      }

      // 헤더 아이콘 화면은 탭 대신 홈 영역에 띄운다.
      // 좌측 메뉴·메뉴검색·위젯 등 다른 경로로 들어와도 같은 자리로 모아
      // 같은 화면이 탭과 홈에 두 번 마운트되지 않게 한다.
      if (HOME_MENU_CODES.includes(menuKey)) {
        openHomeMenu(menuKey, menuTitle, cd_module, filePath, data, menuPath);
        return;
      }

      // 판정은 tabs state가 아니라 동기 ref로 — 커밋 전 오픈도 중복으로 잡힌다
      const existingTabIndex = tabKeysRef.current.indexOf(menuKey);
      if (existingTabIndex !== -1) {
        if (nodeData && figure) {
          const resolvedPath = resolveModulePath(filePath);

          if (!resolvedPath) {
            console.error(`Component not found for path: ${filePath}`);
            return;
          }

          const Component = (modules(resolvedPath) as ModuleType).default;

          const Menuprops: menuInfo = {
            cd_menu: menuKey,
            nm_menu: menuTitle,
            cd_module: cd_module,
            ...(nodeData ? { figure, nodeData } : {}),
            originMenu: originMenu ?? "",
          };

          // 기존 탭을 찾아서 메뉴프롭스만 업데이트
          setTabs((prevTabs) => {
            return prevTabs.map((tab) =>
              tab.key === menuKey
                ? {
                    ...tab,
                    component: (
                      <Suspense fallback={<div>Loading...</div>}>
                        <Component
                          ref={componentRefs[existingTabIndex]}
                          Menuprops={Menuprops}
                        />
                      </Suspense>
                    ),
                  }
                : tab
            );
          });

          // 다른 상태 업데이트 (탭 관련)
          setActiveTab(existingTabIndex); // 기존 탭의 인덱스
          setTabHeight("calc(100% - 20px)");
          setDashHeight("0px");
          setPath(menuPath);

          setPathMap((prev) => ({ ...prev, [existingTabIndex]: menuPath }));
        } else {
          // 이미 존재하는 탭을 활성화
          setActiveTab(existingTabIndex);
          setTabHeight("calc(100% - 20px)");
          setDashHeight("0px");
          setPath(menuPath);
          updateMenuUrl(menuKey);
        }
      } else {
        const resolvedPath = resolveModulePath(filePath);

        if (!resolvedPath) {
          console.error(`Component not found for path: ${filePath}`);
          return;
        }

        const Component = (modules(resolvedPath) as ModuleType).default;

        const Menuprops: menuInfo = {
          cd_menu: menuKey,
          nm_menu: menuTitle,
          cd_module: cd_module,
          ...(nodeData ? { figure, nodeData } : {}),
          originMenu: originMenu ?? "",
        };

        const newComponentRef = createComponentRef();
        // 새 탭 인덱스는 state(tabs.length)가 아니라 동기 사본 기준으로 잡고,
        // 커밋 전이라도 뒤이은 클릭이 중복으로 판정되도록 키를 즉시 넣어 둔다.
        const newTabIndex = tabKeysRef.current.length;
        tabKeysRef.current = [...tabKeysRef.current, menuKey];
        // 새로운 탭 생성
        const newTab: TabItem = {
          key: menuKey,
          title: menuTitle,
          originTitle: menuTitle,
          component: (
            <Suspense fallback={<div>Loading...</div>}>
              <Component
                ref={newComponentRef}
                Menuprops={Menuprops}
                onMenuClick={handleMenuClick}
              />
            </Suspense>
          ),
          closeable: true,
        };

        setTabs((prevTabs) => [...prevTabs, newTab]);
        setMenuDatas((prevMenudata) => [...prevMenudata, data]);
        setComponentRefs((prevComponentRefs) => [
          ...prevComponentRefs,
          newComponentRef,
        ]);
        setActiveTab(newTabIndex);
        setTabHeight("calc(100% - 20px)");
        setDashHeight("0px");
        setPath(menuPath);
        updateMenuUrl(menuKey);

        setPathMap((prev) => ({ ...prev, [newTabIndex]: menuPath }));

        // 핀 고정해 둔 메뉴는 열자마자 고정 구역(맨 앞)으로 보낸다.
        // pathMap까지 반영한 뒤에 옮겨야 경로가 같이 따라간다.
        if (pinnedKeys.includes(menuKey)) {
          const pinnedCount = tabKeysRef.current.filter(
            (key) => key !== menuKey && pinnedKeys.includes(key)
          ).length;
          moveTab(newTabIndex, pinnedCount);
        }
      }
    },
    [
      updateMenuUrl,
      createComponentRef,
      componentRefs,
      openHomeMenu,
      pinnedKeys,
      moveTab,
      userInfo.cd_firm,
      userInfo.cd_user,
      userInfo.dc_ip,
    ]
  );

  useEffect(() => {
    handleMenuClickRef.current = handleMenuClick;
  }, [handleMenuClick]);

  // 핀 목록은 브라우저에 저장해 새로고침·재로그인 후에도 유지한다
  useEffect(() => {
    try {
      localStorage.setItem(PINNED_TABS_KEY, JSON.stringify(pinnedKeys));
    } catch {
      // 저장 실패(사생활 보호 모드 등)는 기능을 막을 이유가 없다
    }
  }, [pinnedKeys]);

  // URL에 포함된 메뉴코드로 직접 진입 시 탭 오픈 처리
  useEffect(() => {
    if (!menuItems.length) return;
    if (!location.pathname.startsWith("/menu/")) return;
    if (sessionStorage.getItem(MENU_ROUTE_ALLOW_KEY) === SESSION_FLAG_TRUE) {
      // 로그인 직후 /menu 재진입용 플래그는 다른 세션 플래그보다 우선 처리
      sessionStorage.removeItem(MENU_ROUTE_ALLOW_KEY);
      sessionStorage.removeItem(SKIP_MENU_ROUTE_KEY);
      sessionStorage.removeItem(JUST_LOGGED_IN_KEY);
    } else {
    }
    if (
      sessionStorage.getItem(SKIP_MENU_ROUTE_KEY) === SESSION_FLAG_TRUE ||
      sessionStorage.getItem(JUST_LOGGED_IN_KEY) === SESSION_FLAG_TRUE
    ) {
      sessionStorage.removeItem(SKIP_MENU_ROUTE_KEY);
      sessionStorage.removeItem(JUST_LOGGED_IN_KEY);
      updateMenuUrl();
      return;
    }
    // 최초 진입에서만 URL 메뉴 오픈 처리
    if (tabs.length > 0) return;

    const menuId = location.pathname.replace("/menu/", "").trim();
    if (!menuId || menuRouteRef.current === menuId) return;
    if (tabs.some((tab) => tab.key === menuId)) {
      menuRouteRef.current = menuId;
      return;
    }

    const timeoutId = window.setTimeout(() => {
      // 150ms 사이에 좌측 메뉴 클릭 등으로 이미 열렸을 수 있다(tabs state로는 못 잡는다)
      if (tabKeysRef.current.includes(menuId)) {
        menuRouteRef.current = menuId;
        return;
      }
      const target = menuItems.find((item) => item.cd_menu === menuId);
      if (!target) return;

      const filePath =
        `./${target.cd_module}/${target.nm_file}/${target.nm_file}`.toUpperCase();

      menuRouteRef.current = menuId;
      handleMenuClick(
        target.cd_menu,
        target.nm_menu,
        target.cd_module,
        filePath,
        target,
        target.nm_path || ""
      );
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [location.pathname, menuItems, handleMenuClick, updateMenuUrl]);

  // 공유 payload가 있을 때 자동으로 메뉴를 열고 상태를 적용
  useEffect(() => {
    // 공유 링크로 전달된 payload가 있으면 해당 메뉴를 자동으로 열고 상태를 주입
    const openSharedMenu = async () => {
      if (!pendingPayload || !pendingPayload.menuId) return;
      // 텔레그램 공유 링크면 메뉴 오픈 전에 바로 플래그 세팅
      if (
        pendingPayload.shareType === "telegram_issue_reg" &&
        pendingPayload.menuId === "SN_SFA_ISSUE_REG_002"
      ) {
        setTelegramShareMenuId(pendingPayload.menuId);
      }

      const target = menuItems.find(
        (item) => item.cd_menu === pendingPayload.menuId
      );
      if (!target) return;

      const filePath =
        `./${target.cd_module}/${target.nm_file}/${target.nm_file}`.toUpperCase();
      await handleMenuClick(
        target.cd_menu,
        target.nm_menu,
        target.cd_module,
        filePath,
        target,
        target.nm_path || ""
      );

      // 공유 상태 주입 재시도.
      // 기존 5회(약 1초)로는 조회패널 등록·조회컨트롤 로딩이 늦는 화면을 놓치므로
      // 공통 폴백을 위해 예산을 늘린다(약 5초). 먼저 성공하면 즉시 종료된다.
      const MAX_FALLBACK_TRIES = 25;
      const tryApplyShareState = async (tries = 0) => {
        const tabIndex = tabs.findIndex((t) => t.key === target.cd_menu);
        if (tabIndex === -1) {
          if (tries < MAX_FALLBACK_TRIES) {
            setTimeout(() => tryApplyShareState(tries + 1), 200);
          } else {
            // 공유 payload가 영구히 남아 자동 오픈을 반복하지 않도록 정리
            clearPendingPayload();
          }
          return;
        }

        const ref = componentRefs[tabIndex]?.current as any;
        if (ref && typeof ref.applyShareState === "function") {
          ref.applyShareState(pendingPayload.state || {}, pendingPayload);
          setActiveTab(tabIndex);
          // 공유 상태 적용 완료 후에도 플래그 유지
          if (
            pendingPayload.shareType === "telegram_issue_reg" &&
            pendingPayload.menuId === "SN_SFA_ISSUE_REG_002"
          ) {
            setTelegramShareMenuId(pendingPayload.menuId);
          }
          clearPendingPayload();
          return;
        }

        // 개별 applyShareState가 없는 화면은 공통 경로로 조회조건을 복원한다.
        // 챗봇(applySearchParamsAndRun)과 동일한 방식 — 조회패널에 값을 주입하고
        // 화면의 onSearch를 인자 없이 호출한다. 화면별 추가 구현이 필요 없다.
        // (인자를 넘기면 onSearch(isInit)/onSearch(saveFlag) 형태의 화면이 오동작한다)
        const panel = onSearchStore.getState().chatSearchPanels[target.cd_menu];
        // SearchPanel은 조회컨트롤이 도착하기 전에도(메뉴코드만으로) 먼저 등록된다.
        // 그 시점에 주입하면 뒤늦게 도착한 화면 초기값(setFormData → data prop 재동기화)에
        // 덮어써져 "됐다 안됐다"가 되므로, 컨트롤이 실제로 준비된 뒤에만 주입한다.
        const panelReady = (panel?.getAllowedFieldNames?.() || []).length > 0;
        if (ref && typeof ref.onSearch === "function" && panel && panelReady) {
          const payloadKey = `${pendingPayload.menuId}|${pendingPayload.createdAt}`;
          // effect가 tabs/componentRefs 변화로 재실행될 수 있어 중복 조회를 막는다
          if (shareFallbackKeyRef.current !== payloadKey) {
            shareFallbackKeyRef.current = payloadKey;
            setActiveTab(tabIndex);
            await applySharedSearchAndRun(panel, ref, pendingPayload);
          }
          clearPendingPayload();
          return;
        }

        // 아직 ref/조회패널이 준비되지 않은 단계 — 예산까지 기다린다
        if (tries < MAX_FALLBACK_TRIES) {
          setTimeout(() => tryApplyShareState(tries + 1), 200);
        } else {
          // 조회패널이 없는 화면 등은 메뉴 오픈까지만 하고 payload를 정리한다
          clearPendingPayload();
        }
      };

      setTimeout(() => tryApplyShareState(0), 200);
    };

    openSharedMenu();
  }, [
    pendingPayload,
    menuItems,
    tabs,
    componentRefs,
    handleMenuClick,
    clearPendingPayload,
  ]);
  // 자바스크립트 윈도우 객체를 이용 전역객체로 사용할 수 있음
  (window as any).onActivate = handleMenuClick;
  /* 챗봇 메뉴 닫기(CLOSE_MENU) — 여는 것(onActivate)과 같은 방식으로 내준다 (2026-10-16) */
  (window as any).onCloseMenus = closeMenusForChat;

  // 활성 탭이 이슈등록 화면인지 판별 (모바일 UI 분기용)
  const isIssueMenuView =
    menuDatas[activeTab]?.cd_menu === "SN_SFA_ISSUE_REG_002";

  // 탭 활성화. 클릭뿐 아니라 드래그를 시작할 때도 쓴다.
  // (브라우저 탭처럼, 끌기 시작한 탭이 곧 보고 있는 탭이 되어야 한다)
  const activateTab = (index: number) => {
    setActiveTab(index);
    setPath(pathMap[index] ? pathMap[index] : "");
    updateMenuUrl(tabs[index]?.key);
  };

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    activateTab(newValue);
  };

  const handleTabClick = (event: MouseEvent, index: number) => {
    setTabHeight("calc(100% - 20px)");
    setDashHeight("0px");
    setPath(pathMap[index]);
    updateMenuUrl(tabs[index]?.key);
  };

  const handleRightClick = (event: MouseEvent) => {
    event.preventDefault();
    if (!tabsRef.current) return;

    const { clientX: x, clientY: y } = event;
    const tabsArea = tabsRef.current.getBoundingClientRect();
    const isInsideTabs =
      x >= tabsArea.left &&
      x <= tabsArea.right &&
      y >= tabsArea.top &&
      y <= tabsArea.bottom;

    if (!isInsideTabs) {
      handleMenuClose();
      return;
    }

    const clickedTabIndex = getClickedTabIndex(x, y);

    if (clickedTabIndex === -1) {
      // 빈 공간: 탭 변경하지 않고 보고 있던 탭을 기준으로 컨텍스트 메뉴만 표시
      setContextTabIndex(activeTab);
      setMenuPosition({ mouseX: x, mouseY: y });
      return;
    }

    setContextTabIndex(clickedTabIndex);
    setActiveTab(clickedTabIndex);
    setTabHeight("calc(100% - 20px)");
    setDashHeight("0px");
    setPath(pathMap[clickedTabIndex] ?? "");
    setMenuPosition({ mouseX: x, mouseY: y });
  };

  const handleMenuClose = () => {
    setMenuPosition(null);
    setOpenListAnchor(null);
  };

  /** 우클릭 기준 탭(빈 공간에서 눌렀으면 보고 있던 탭) */
  const contextTab = tabs[contextTabIndex] ?? null;

  /** 핀 고정된 탭은 일괄 닫기에서 제외한다(하나씩 닫는 건 그대로 된다) */
  const closableKeys = (targets: TabItem[]) =>
    targets
      .filter((tab) => !pinnedKeys.includes(tab.key))
      .map((tab) => tab.key);

  const closeContextTab = () => {
    handleMenuClose();
    if (contextTab) void closeMenus([contextTab.key]);
  };

  const closeTabsToTheRight = () => {
    handleMenuClose();
    if (contextTabIndex < 0) return;
    void closeManyTabs(closableKeys(tabs.slice(contextTabIndex + 1)));
  };

  const closeOtherTabs = () => {
    handleMenuClose();
    if (!contextTab) return;
    void closeManyTabs(
      closableKeys(tabs.filter((tab) => tab.key !== contextTab.key))
    );
  };

  const closeAllTabs = () => {
    handleMenuClose();
    void closeManyTabs(closableKeys(tabs));
  };

  const toggleContextTabPin = () => {
    handleMenuClose();
    if (!contextTab) return;

    const key = contextTab.key;
    const from = tabs.findIndex((tab) => tab.key === key);
    // 자기 자신을 뺀 고정 탭 수 = 고정 구역의 끝자리.
    // 고정할 땐 그 자리로 들어가고, 해제할 땐 고정 구역 바로 뒤로 나온다.
    const target = tabs.filter(
      (tab) => tab.key !== key && pinnedKeys.includes(tab.key)
    ).length;

    setPinnedKeys((prev) =>
      prev.includes(key)
        ? prev.filter((pinnedKey) => pinnedKey !== key)
        : [...prev, key]
    );

    if (from >= 0 && from !== target) {
      moveTab(from, Math.min(target, tabs.length - 1));
    }
  };

  /** 열린 메뉴 목록에서 선택 — 홈 화면을 보고 있었어도 탭 화면으로 전환한다 */
  const activateTabFromList = (index: number) => {
    handleMenuClose();
    setTabHeight("calc(100% - 20px)");
    setDashHeight("0px");
    activateTab(index);
  };

  const handleMenuRightClick = (event: React.MouseEvent) => {
    event.preventDefault();
    handleMenuClose();
    handleRightClick(event);
  };

  // 클릭한 탭의 인덱스를 계산하는 함수
  const getClickedTabIndex = (x: number, y: number) => {
    if (!tabsRef.current) return -1;
    const tabEls = tabsRef.current.querySelectorAll('[role="tab"]');
    for (let i = 0; i < tabEls.length; i++) {
      const rect = (tabEls[i] as HTMLElement).getBoundingClientRect();
      if (
        x >= rect.left &&
        x <= rect.right &&
        y >= rect.top &&
        y <= rect.bottom
      ) {
        return i;
      }
    }
    return -1; // 빈 공간
  };

  // Resize 실시간으로 width를 저장하는 메서드
  const handleResize: ResizeCallback = (
    event: globalThis.MouseEvent | globalThis.TouchEvent,
    direction: Direction,
    elementRef: HTMLElement,
    delta: NumberSize
  ) => {
    setWidth(elementRef.offsetWidth);
    setLogoWidth(elementRef.offsetWidth);
    leftMenuRef.current?.setColumnWidths();
  };

  // resize 종류 이후 prevWidth에 저장하는 메서드(메뉴 toggle에 사용)
  const handleResizeStop: ResizeCallback = (
    event: globalThis.MouseEvent | globalThis.TouchEvent,
    direction: Direction,
    elementRef: HTMLElement,
    delta: NumberSize
  ) => {
    setPrevWidth(elementRef.offsetWidth);
  };

  /**
   * 메인화면 로고 클릭 시 메뉴 toggle 기능
   * @param isMenuVisible 메뉴표출여부(true/false)
   */
  const changeMenuWidth = (isMenuVisible: boolean) => {
    if (isMenuVisible) {
      setWidth(prevWidth);
    } else {
      setWidth(0);
    }
    leftMenuRef.current?.setColumnWidths();
  };

  /**
   * Header의 home 버튼 클릭 시 dashboard 열기
   */
  const openDashBoard = () => {
    setTabHeight("50px");
    setDashHeight("100%");
    setPath("");
    setActiveTab(-1);
    setActiveHomeKey(null); // 기본 대시보드로 복귀
    if (isChatbotRoute) {
      navigate("/MainLayout");
    }
  };

  /**
   * 데이터 조회, 수정 시 spinner 상태 관리를 위한 메서드
   * @param cd_menu 메뉴 코드
   * @param state 로딩 상태
   */
  const handleIsLoading = (cd_menu: string, state: boolean) => {
    // 값이 그대로면 이전 Map을 그대로 돌려줘서 불필요한 리렌더를 끊는다.
    // (탭이 여러 개면 이런 헛 업데이트가 탭 수만큼 증폭돼 중첩 업데이트 한도를 넘긴다)
    setLoadingMenu((prev) => {
      if (state) {
        if (prev.get(cd_menu) === true) return prev;
        const newMap = new Map(prev);
        newMap.set(cd_menu, true);
        return newMap;
      }
      if (!prev.has(cd_menu)) return prev;
      const newMap = new Map(prev);
      newMap.delete(cd_menu);
      return newMap;
    });
  };

  /**
   * 데이터 조회, 수정 시 tab title spinner 관리를 위한 메서드
   * @param cd_menu 메뉴 코드
   * @param state 로딩 상태
   */
  const handleTabTitle = (cd_menu: string, state: boolean) => {
    setTabs((prevTabs) => {
      // 이미 같은 상태면 tabs 배열을 새로 만들지 않는다.
      // 새 배열을 반환하면 tabs identity가 바뀌어 레이아웃 전체가 리렌더되고,
      // 탭이 많을수록 이 헛 업데이트가 겹쳐 "Maximum update depth"로 번진다.
      const target = prevTabs.find((tab) => tab.key === cd_menu);
      if (!target) return prevTabs;
      const isSpinnerNow = target.title !== target.originTitle;
      if (isSpinnerNow === state) return prevTabs;

      return prevTabs.map((tab) => {
        if (tab.key === cd_menu) {
          const newTitle = state ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <CircularProgress
                size={12}
                thickness={6}
                sx={{ color: "white" }}
              />
              <span>{tab.originTitle}</span>
            </Box>
          ) : (
            tab.originTitle
          );

          return {
            ...tab,
            title: newTitle,
          };
        }
        return tab;
      });
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100vw",
        // overflowX: "auto",
      }}
    >
      <DeployNotificationListener />
      <FcmPushInitializer />
      {/* Header */}
      <Header
        path={isChatbotRoute ? "SNotes AI Bot" : path}
        changeMenuWidth={changeMenuWidth}
        openDashBoard={openDashBoard}
        width={logoWidth}
        onMenuClick={handleMenuClick}
        {...(userInfo?.yn_ai === "Y"
          && !isChatbotRoute
          /* 상단 챗봇 버튼은 열고 닫기를 오간다 — 떠 있는 창은 바깥을 눌러도 안 닫힌다 (2026-10-21) */
          ? { onChatbotOpen: () => setIsChatbotDrawerOpen((open) => !open) }
          : {})}
      />

      {/* Main Content */}
      <Box sx={{ display: "flex", height: "calc(100vh - 60px)", border: 0 }}>
        {/* Left Menu */}
        <Resizable
          // 이슈등록 화면은 모바일에서 좌측 메뉴 숨김
          className={isIssueMenuView ? "hidden md:block" : ""}
          size={{ width, height: "100%" }} // 크기 설정
          // minWidth={250} // 최소 width 설정
          maxWidth={400} // 최대 width 설정
          onResize={handleResize} // 크기 조정 실시간으로 상태 업데이트
          onResizeStop={handleResizeStop} // 크기 조정이 끝난 후 상태 업데이트
          enable={{ right: true }} // 오른쪽으로만 크기 조정 가능
          // resize handler 스타일 옵션
          // handleStyles={{
          //   right: {
          //     background: grey[300], // 리사이징 핸들러 배경색
          //     width: '5px',      // 핸들러의 너비
          //     height: '100%',     // 핸들러의 높이
          //     cursor: 'ew-resize', // 커서 모양 (가로 리사이징)
          //   },
          // }}
        >
          <LeftMenu
            ref={leftMenuRef}
            width={width}
            onMenuClick={handleMenuClick}
          />
        </Resizable>

        {/* Right Content */}
        <div>
          <Box
            sx={{
              flex: 1,
              backgroundColor: "#f5f5f5",
              // 탭도, 헤더 아이콘 화면도 없으면 빈 탭 바 영역을 숨김.
              // (헤더 아이콘 화면은 탭이 없어도 툴바(조회/저장)가 필요해 바를 남긴다)
              display:
                isChatbotRoute || (tabs.length === 0 && !activeHomeView)
                  ? "none"
                  : "block",
              height: isChatbotRoute ? "0px" : tabHeight, // Full viewport height
              // 이슈등록 화면은 모바일에서 좌측 메뉴를 숨기므로 폭을 100%로 확장
              width: isIssueMenuView
                ? { xs: "100vw", md: `calc(100vw - ${width}px)` }
                : `calc(100vw - ${width}px)`,
            }}
          >
            <TabContext value={String(activeTab)}>
              {/* Tabs */}
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: "rgb(186,191,199)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <TabBar
                  tabs={tabs}
                  activeTab={activeTab}
                  containerRef={tabsRef}
                  onContextMenu={handleRightClick}
                  onTabChange={handleTabChange}
                  onTabClick={handleTabClick}
                  onTabClose={handleTabClose}
                  onTabActivate={activateTab}
                  onMoveTab={moveTab}
                  pinnedKeys={pinnedKeys}
                />
                {/* 오른쪽: 커스텀 버튼 */}
                <Box
                  sx={{
                    width: 350,
                    textAlign: "right",
                  }}
                >
                  {/* 헤더 아이콘 화면이 떠 있으면 툴바를 그 화면 기능에 연결한다 */}
                  <TabToolbar
                    index={activeHomeView ? 0 : activeTab}
                    componentRefs={homeToolbarRefs ?? componentRefs}
                    menuData={homeToolbarMenuData ?? menuDatas}
                    isLoading={handleIsLoading}
                    tabTitle={handleTabTitle}
                    loadingMenu={loadingMenu}
                  />
                </Box>
              </Box>

              {/* Tab Panels */}
              {/* Tab Panels (Always mounted; only active is interactive) */}
              <div
                // 탭 콘텐츠 영역 랩퍼: 기존 높이/폭을 그대로 사용
                style={{
                  position: "relative",
                  height: "calc(100% - 50px)",
                  width: "100%",
                  flex: 1,
                }}
              >
                {memoizedTabs.map((tab, index) => {
                  const isActive = activeTab === index;
                  // 조회/저장 중이거나 화면 초기화가 아직 안 끝난 탭은 spinner로 덮는다
                  const isTabLoading =
                    loadingMenu.has(tab.key) ||
                    (initializingMenus[tab.key] || 0) > 0;
                  return (
                    <div
                      className="tab-panel"
                      key={tab.key}
                      aria-hidden={!isActive}
                      style={{
                        // 모든 패널을 겹쳐 놓고 활성 패널만 보이도록
                        position: "absolute",
                        inset: 0, // top:0, right:0, bottom:0, left:0
                        // 시각/입력 토글
                        opacity: isActive ? 1 : 0,
                        visibility: isActive ? "visible" : "hidden",
                        pointerEvents: isActive ? "auto" : "none",
                        transition: "opacity 120ms linear",
                        // 기존 스타일 가져오기
                        height: "100%",
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          overflowY: "auto",
                          height: "100%",
                          width: "100%",
                          paddingTop: "5px",
                          paddingBottom: "5px",
                          paddingRight: "20px",
                          paddingLeft: "16px",
                        }}
                      >
                        <div
                          className="h-full flex flex-col"
                          style={{ position: "relative" }} //
                        >
                          {isTabLoading && (
                            <>
                              {/* 클릭 차단용 얇은 오버레이 */}
                              <div
                                style={{
                                  position: "absolute",
                                  inset: 0,
                                  zIndex: 50,
                                  background: "transparent",
                                }}
                              />
                              {/* 스피너는 오버레이보다 위로 */}
                              <div
                                style={{
                                  position: "absolute",
                                  inset: 0,
                                  zIndex: 51,
                                }}
                              >
                                <CommonSpinner isLoading={isTabLoading} />
                              </div>
                            </>
                          )}
                          {/* 챗봇 행복사 중 — 이 메뉴를 덮고 "행 복사 중 · N / 전체건" (2026-10-16) */}
                          <ChatCopyProgress cdMenu={tab.key} />
                          {/* 한 화면의 오류가 레이아웃째로 앱을 내리지 않도록 격리한다 */}
                          <ErrorBoundary label={tab.key}>
                            <TabContent
                              component={
                                tab.component || <div>No content available</div>
                              }
                            />
                          </ErrorBoundary>
                        </div>
                      </Box>
                    </div>
                  );
                })}
              </div>
            </TabContext>
          </Box>

          {isChatbotRoute ? (
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#f5f5f5",
                height: "calc(100vh - 80px)",
                overflow: "auto",
                width: isIssueMenuView
                  ? { xs: "100vw", md: `calc(100vw - ${width}px)` }
                  : `calc(100vw - ${width}px)`,
              }}
            >
              <Chatbot />
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#f5f5f5",
                position: "relative", // 홈 패널들을 겹쳐 놓기 위한 기준
                overflow: "hidden", // 스크롤은 각 패널이 담당한다
                // 탭 바가 숨겨질 때만 그 높이를 빼지 않고 전체 사용
                height:
                  tabs.length === 0 && !activeHomeView
                    ? "calc(100% - 20px)"
                    : `calc(${dashHeight} - ${tabHeight} - 20px)`,
                width: isIssueMenuView
                  ? { xs: "100vw", md: `calc(100vw - ${width}px)` }
                  : `calc(100vw - ${width}px)`,
              }}
            >
              {/* 기본 대시보드와 헤더 아이콘 화면을 겹쳐 두고 활성만 보여준다 */}
              <div style={homePaneStyle(activeHomeKey === null)}>
                <ErrorBoundary label="Dashboard">
                  <Dashboard onMenuClick={handleMenuClick} />
                </ErrorBoundary>
              </div>
              {homeViews.map((view) => (
                <div
                  key={view.key}
                  style={{
                    ...homePaneStyle(activeHomeKey === view.key),
                    padding: "5px 20px 5px 16px",
                  }}
                >
                  <div
                    className="h-full flex flex-col"
                    style={{ position: "relative" }}
                  >
                    <ErrorBoundary label={view.key}>
                      {view.component}
                    </ErrorBoundary>
                  </div>
                </div>
              ))}
            </Box>
          )}

          {/* Footer */}
          <Footer />
        </div>
      </Box>

      {/* 탭 우클릭 메뉴 */}
      <Menu
        open={menuPosition !== null}
        onClose={handleMenuClose}
        onContextMenu={handleMenuRightClick}
        anchorReference="anchorPosition"
        anchorPosition={
          menuPosition === null
            ? undefined
            : { top: menuPosition.mouseY, left: menuPosition.mouseX }
        }
        disableAutoFocusItem
        slotProps={{ paper: { sx: { ...contextMenuPaperSx, minWidth: 240 } } }}
        MenuListProps={{ dense: true, sx: { py: 0 } }}
      >
        {/* 어떤 탭을 기준으로 동작하는지 먼저 보여준다 */}
        <Box sx={{ ...contextMenuHeaderSx, mb: 0.5 }}>
          {contextTab && pinnedKeys.includes(contextTab.key) ? (
            <PushPinIcon sx={{ fontSize: "1rem" }} />
          ) : (
            <TabIcon sx={{ fontSize: "1rem" }} />
          )}
          <Box
            component="span"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 210,
            }}
          >
            {contextTab?.originTitle ?? "탭 메뉴"}
          </Box>
        </Box>

        <MenuItem
          onClick={closeContextTab}
          disabled={!contextTab}
          sx={contextMenuItemSx}
        >
          <ListItemIcon>
            <CloseIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="메뉴 닫기" />
        </MenuItem>
        <MenuItem
          onClick={closeTabsToTheRight}
          disabled={
            contextTabIndex < 0 ||
            closableKeys(tabs.slice(contextTabIndex + 1)).length === 0
          }
          sx={contextMenuItemSx}
        >
          <ListItemIcon>
            <ArrowForwardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="오른쪽 메뉴 닫기" />
        </MenuItem>
        <MenuItem
          onClick={closeOtherTabs}
          disabled={
            !contextTab ||
            closableKeys(tabs.filter((tab) => tab.key !== contextTab.key))
              .length === 0
          }
          sx={contextMenuItemSx}
        >
          <ListItemIcon>
            <ClearAllIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="이 창을 제외하고 모든 메뉴 닫기" />
        </MenuItem>
        <MenuItem
          onClick={closeAllTabs}
          disabled={closableKeys(tabs).length === 0}
          sx={contextMenuItemSx}
        >
          <ListItemIcon>
            <LayersClearIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="모든 메뉴 닫기" />
        </MenuItem>

        <Divider sx={{ my: 0.5, borderColor: CONTEXT_MENU_BORDER }} />

        <MenuItem
          onClick={toggleContextTabPin}
          disabled={!contextTab}
          sx={contextMenuItemSx}
        >
          <ListItemIcon>
            {contextTab && pinnedKeys.includes(contextTab.key) ? (
              <PushPinIcon fontSize="small" />
            ) : (
              <PushPinOutlinedIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={
              contextTab && pinnedKeys.includes(contextTab.key)
                ? "핀 고정 해제"
                : "핀 고정"
            }
          />
        </MenuItem>

        <Divider sx={{ my: 0.5, borderColor: CONTEXT_MENU_BORDER }} />

        <MenuItem
          onClick={(event) => setOpenListAnchor(event.currentTarget)}
          disabled={tabs.length === 0}
          sx={contextMenuItemSx}
        >
          <ListItemIcon>
            <MenuOpenIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={`열린 메뉴 (${tabs.length})`} />
          <ChevronRightIcon
            fontSize="small"
            sx={{ color: "rgb(151, 151, 151)", ml: 1 }}
          />
        </MenuItem>

        <Box sx={{ ...contextMenuFooterSx, mt: 0.5 }}>
          <span>{`열린 메뉴 ${tabs.length}개`}</span>
          <span>{`고정 ${pinnedKeys.length}개`}</span>
        </Box>
      </Menu>

      {/* 열린 메뉴 목록(하위 메뉴) */}
      <Menu
        open={openListAnchor !== null}
        anchorEl={openListAnchor}
        onClose={() => setOpenListAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: { ...contextMenuPaperSx, minWidth: 220, maxHeight: 360 },
          },
        }}
        MenuListProps={{ dense: true, sx: { py: 0, pb: 0.5 } }}
      >
        <Box sx={{ ...contextMenuHeaderSx, mb: 0.5 }}>
          <MenuOpenIcon sx={{ fontSize: "1rem" }} />
          <Box component="span">{`열린 메뉴 (${tabs.length})`}</Box>
        </Box>
        {tabs.map((tab, index) => (
          <MenuItem
            key={tab.key}
            selected={index === activeTab}
            onClick={() => activateTabFromList(index)}
            sx={{
              ...contextMenuItemSx,
              // 보고 있는 탭 - 헤더 밴드보다 한 톤 진한 배경 + 남색 좌측 바 + 굵은 글씨.
              // 색만으로 구분하지 않도록 오른쪽에 점 표식도 함께 둔다.
              "&.Mui-selected": {
                backgroundColor: CONTEXT_MENU_ACTIVE,
                borderLeftColor: CONTEXT_MENU_NAVY,
                color: CONTEXT_MENU_NAVY,
                "& .MuiListItemText-primary": { fontWeight: "bold" },
                "&:hover": { backgroundColor: CONTEXT_MENU_ACTIVE_HOVER },
              },
            }}
          >
            <ListItemIcon>
              {pinnedKeys.includes(tab.key) ? (
                <PushPinIcon fontSize="small" />
              ) : (
                <DescriptionOutlinedIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={tab.originTitle}
              primaryTypographyProps={{
                sx: {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 220,
                },
              }}
            />
          </MenuItem>
        ))}
      </Menu>
      {/* 떠 있는 창 (2026-10-21) — 바깥을 눌러도 닫히지 않는다(닫기는 ✕). 챗봇을 띄운 채 메뉴 화면을 조작한다.
          덮는 서랍(바깥을 누르면 닫힘)은 화면 조작 중에 계속 닫혀 불편했다(사용자). 메뉴 화면 폭은 그대로, 챗봇 밑은 가려진다.
          persistent 는 닫혀도 내용을 지우지 않는다 — 전처럼 대화가 남는다 */}
      <Drawer
        anchor="right"
        variant="persistent"
        /* /chatbot 화면에서는 본문에 챗봇이 이미 있다 — 서랍 챗봇까지 띄우면 두 인스턴스가 대화방 선택을 나눠 갖는다 (2026-10-08) */
        open={isChatbotDrawerOpen && !isChatbotRoute}
        PaperProps={{
          /* 고정을 끈 상태의 "밖을 누르면 닫기" 가 챗봇 창 안 누름을 가른다 */
          className: "snotes-chatbot-drawer",
          sx: {
            boxShadow: "-8px 0 24px rgba(20, 22, 40, 0.18)",
            height: "calc(100vh - 60px)",
            mt: "60px",
            /* 마우스를 올리거나 입력 중이면 불투명 — 읽고 쓸 때는 비치지 않는다 */
            opacity: 1 - chatbotClear / 100,
            transition: "opacity 0.15s",
            "&:hover, &:focus-within": { opacity: 1 },
            width: isMenuDesignerActive
              ? { xs: "100vw", sm: 620, md: 720 }
              : { xs: "100vw", sm: chatbotWidth },
          },
        }}
      >
        {/* 폭 조절 — 서랍 왼쪽 가장자리를 끈다 (2026-10-16) */}
        {!isMenuDesignerActive && (
          <Box
            aria-label="챗봇 폭 조절"
            onMouseDown={startChatbotResize}
            role="separator"
            sx={{
              bottom: 0,
              cursor: "ew-resize",
              left: 0,
              position: "absolute",
              top: 0,
              width: 6,
              zIndex: 10,
              "&:hover": { background: "rgba(40, 53, 147, 0.15)" },
            }}
          />
        )}
        <Box
          sx={{
            alignItems: "center",
            borderBottom: "1px solid #e1e6ef",
            display: "flex",
            height: 48,
            justifyContent: "space-between",
            px: 2,
          }}
        >
          <Typography fontSize={15} fontWeight={700}>
            SNotes AI Bot v0.10.0
          </Typography>
          <Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
            <ChatTip title={chatbotPinned ? "고정 해제" : "고정"}>
              <IconButton
                aria-label={chatbotPinned ? "챗봇 고정 해제" : "챗봇 고정"}
                aria-pressed={chatbotPinned}
                onClick={toggleChatbotPin}
                size="small"
              >
                {chatbotPinned ? (
                  <PushPinIcon fontSize="small" sx={{ color: "#283593" }} />
                ) : (
                  <PushPinOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </ChatTip>
            <ChatTip title="투명도">
              <IconButton
                aria-label="챗봇 투명도"
                onClick={(e) => setClearAnchor(e.currentTarget)}
                size="small"
              >
                <OpacityIcon fontSize="small" />
              </IconButton>
            </ChatTip>
            <IconButton
              aria-label="SNotes AI Bot 닫기"
              onClick={() => setIsChatbotDrawerOpen(false)}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        {/* 투명도 막대 — 막대를 움직이는 동안 마우스가 챗봇 밖이라 바뀐 투명도가 바로 보인다 */}
        <Popover
          anchorEl={clearAnchor}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          onClose={() => setClearAnchor(null)}
          open={clearAnchor !== null}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
        >
          <Box sx={{ px: 2, py: 1.5, width: 200 }}>
            <Typography color="text.secondary" fontSize={12}>
              투명도 {chatbotClear}%
            </Typography>
            <Slider
              aria-label="투명도"
              marks
              max={60}
              min={0}
              onChange={(_, v) => setChatbotClear(v as number)}
              onChangeCommitted={(_, v) => saveChatbotClear(v as number)}
              size="small"
              step={10}
              value={chatbotClear}
            />
          </Box>
        </Popover>
        <Box
          sx={{
            height: "calc(100% - 48px)",
            "& .chatbot-page": {
              background: "transparent",
              height: "100%",
              minHeight: 0,
              padding: 0,
            },
            "& .chatbot-shell": {
              border: 0,
              borderRadius: 0,
              minHeight: 0,
            },
            // 좌측 작업 메뉴는 드로어 안에서 대화창과 맞붙는다(경계선 하나만 남긴다)
            "& .chatbot-sidebar": {
              border: 0,
              borderRadius: 0,
              borderRight: "1px solid #e1e6ef",
            },
          }}
        >
          {/* 서랍은 keepMounted 라 늘 떠 있다. /chatbot 에서는 렌더하지 않아 챗봇을 하나로 유지한다 */}
          {!isChatbotRoute && <Chatbot />}
        </Box>
      </Drawer>
    </Box>
  );
}
